import { Injectable } from "@angular/core";
import { JwtHelperService } from "@auth0/angular-jwt";

import { AuthenticationToken } from "../model/authentication-token.model";
import { BackendPermission } from "../model/backend-permission.enum";
import { Logger } from "../model/logger.model";
import { Optional } from "../support/optional.model";
import { EnvironmentService } from "./environment.service";
import { LocalStorageService } from "./local-storage.service";
import { LoggingService } from "./logging.service";

    /**
     * @description
     *  Servicio de autenticación principal.
     */

@Injectable({ providedIn: "root" })
export class AuthenticationService {

    /**
     * @description
     *  El token actual activo.
     */
    private authenticationToken: AuthenticationToken = undefined;

    /**
     * @description
     *  Logger principal del servicio.
     */
    private logger: Logger = this.loggingService.getLogger("authentication.service");

    constructor(
        private environmentService: EnvironmentService,
        private localStorageService: LocalStorageService,
        private loggingService: LoggingService) {}

	/**
	 * @description
	 *	Determina si el principal posee el permiso indicado. Reifica el token
	 *	de ser necesario, antes de determinar la posesión del permiso
	 *	indicado.
	*/
	public hasPermission(permission: BackendPermission): boolean {
		this.token();
		return Optional.of(this.authenticationToken)
			.map(token => token.permissions.has(permission))
			.getOrElse(false);
	}

    /**
     * @description
     *  Cierra la sesión del usuario actual, invalidando el token asociado del
     *  almacenamiento local.
     */
    public logout(): void {
        this.logger.debug(`Logging out UID:'${this.authenticationToken.uid}'...`);
        this.localStorageService.remove(this.environmentService.authenticationTokenKey);
        if (this.authenticationToken !== undefined) {
            const name: string = this.authenticationToken.nam;
            this.authenticationToken = undefined;
            this.logger.debug(`Logged out. Bye ${name}!`);
        }
        else {
            this.logger.debug("Logged out.");
        }
    }

    /**
     * @description
     *  Obtiene el token actual y lo transforma en un modelo reificado luego
     *  de decodificarlo. Debido a que el proceso de decodificación es
     *  costoso, el resultado de la misma se memoizará hasta que el token
     *  quede invalidado o hasta que se obtenga uno nuevo.
     *
     * @returns
     *  El token de autenticación actual, reificado.
     */
    public token(): AuthenticationToken {
        if (this.isInvalid()) {
            this.logger.debug("Authentication token reification...");
            const accessKey = this.environmentService.authenticationTokenKey;
            const token = this.localStorageService.getString(accessKey);
            if (token === undefined) {
                throw new Error(
                    `No existe un token de autenticación almacenado en 'localStorage'. La clave de acceso fue: '${accessKey}'.`);
            }
            else {
                const jwtHelperService = new JwtHelperService();
                const decodedToken = jwtHelperService.decodeToken(token);
                this.authenticationToken = AuthenticationToken.from(decodedToken);
            }
            this.logger.debug(`Token reificated. Hi ${this.authenticationToken.nam}!`);
        }
        return this.authenticationToken;
    }

    /**
     * @description
     *  Determina si el token actual es inválido o no.
     *
     * @returns
     *  Verdadero si el token expiró o está indefinido. Falso de otro modo,
     *  con lo cual el mismo sigue siendo válido.
     */
    private isInvalid(): boolean {
        return this.authenticationToken === undefined
            || this.authenticationToken.isExpired();
    }
}
